"use client"

import { createContext, useCallback, useContext, useState } from "react"

import type { LoginContextType, LoginState } from "./types"

const INITIAL_STATE: LoginState = {
  email: "",
  isLoading: false,
  isGoogleLoading: false,
  isMagicLinkSent: false,
  error: null,
}

const LoginContext = createContext<LoginContextType | null>(null)

interface LoginProviderProps {
  children: React.ReactNode
}

export function LoginProvider({ children }: LoginProviderProps) {
  const [state, setState] = useState<LoginState>(INITIAL_STATE)

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email }))
  }, [])

  const setIsLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }))
  }, [])

  const setIsGoogleLoading = useCallback((isGoogleLoading: boolean) => {
    setState((prev) => ({ ...prev, isGoogleLoading }))
  }, [])

  const setIsMagicLinkSent = useCallback((isMagicLinkSent: boolean) => {
    setState((prev) => ({ ...prev, isMagicLinkSent }))
  }, [])

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }))
  }, [])

  const reset = useCallback(() => {
    setState(INITIAL_STATE)
  }, [])

  return (
    <LoginContext.Provider
      value={{
        ...state,
        setEmail,
        setIsLoading,
        setIsGoogleLoading,
        setIsMagicLinkSent,
        setError,
        reset,
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}

export function useLoginContext() {
  const context = useContext(LoginContext)
  if (!context) {
    throw new Error("useLoginContext must be used within LoginProvider")
  }
  return context
}
